import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Scale, ChevronDown } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  const isActive = (path) => location.pathname === path;

  const navLinks = [
    { href: '/', label: 'Home' },
    { href: '/llc-usa-gruenden', label: 'LLC Gründung USA' },
    { href: '/ltd-uk-gruenden', label: 'LTD Gründung UK' },
    { href: '/firma-gruenden-kanada', label: 'Firma Gründen Kanada' },
    { href: '/registered-agent', label: 'Registered Agent' },
  ];

  const ratgeberLinks = [
    { href: '/ratgeber/llc-wyoming-gruenden', label: 'LLC in Wyoming gründen' },
    { href: '/ratgeber/llc-delaware-gruenden', label: 'LLC in Delaware gründen' },
    { href: '/ratgeber/ein-nummer-beantragen', label: 'EIN Nummer beantragen' },
    { href: '/ratgeber/us-bankkonto-eroeffnen-auslaender', label: 'US-Bankkonto eröffnen' },
    { href: '/ratgeber/beste-llc-gruendungsservices-vergleich', label: 'LLC-Services Vergleich' },
  ];

  return (
    <header 
      className="sticky top-0 z-50 w-full backdrop-blur-xl bg-white/85 border-b border-slate-200"
      data-testid="header"
    >
      <div className="container-wide">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center gap-2 group"
            data-testid="logo-link"
          >
            <div className="flex items-center justify-center w-8 h-8 bg-teal-600 rounded-sm">
              <Scale className="w-5 h-5 text-white" />
            </div>
            <span className="font-heading font-bold text-xl text-navy-900">
              ReviewLex<span className="text-slate-400 font-normal">.de</span>
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={`px-4 py-2 text-sm font-medium rounded-sm transition-colors ${
                  isActive(link.href)
                    ? 'text-teal-600 bg-teal-50'
                    : 'text-slate-700 hover:text-teal-600 hover:bg-slate-50'
                }`}
                data-testid={`nav-link-${link.href.replace(/\//g, '-')}`}
              >
                {link.label}
              </Link>
            ))}

            {/* Ratgeber Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger className="px-4 py-2 text-sm font-medium text-slate-700 hover:text-teal-600 hover:bg-slate-50 rounded-sm transition-colors flex items-center gap-1">
                Ratgeber
                <ChevronDown className="w-4 h-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64">
                {ratgeberLinks.map((link) => (
                  <DropdownMenuItem key={link.href} asChild>
                    <Link to={link.href} className="cursor-pointer">
                      {link.label}
                    </Link>
                  </DropdownMenuItem>
                ))}
                <DropdownMenuItem asChild>
                  <Link to="/ratgeber" className="cursor-pointer font-medium text-teal-600">
                    Alle Ratgeber ansehen →
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </nav>

          {/* Right side items */}
          <div className="hidden lg:flex items-center gap-4">
            <Link
              to="/ueber-uns"
              className="text-sm font-medium text-slate-700 hover:text-teal-600 transition-colors"
              data-testid="nav-about"
            >
              Über Uns
            </Link>
            <Link
              to="/kontakt"
              className="text-sm font-medium text-slate-700 hover:text-teal-600 transition-colors"
              data-testid="nav-contact"
            >
              Kontakt
            </Link>
            <Link
              to="/northwest-registered-agent-erfahrungen"
              className="cta-primary text-sm"
              data-testid="cta-header"
            >
              Testsieger ansehen
            </Link>
          </div>

          {/* Mobile menu button */}
          <button
            className="lg:hidden p-2 text-slate-700 hover:text-slate-900"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="lg:hidden py-4 border-t border-slate-200" data-testid="mobile-nav">
            <nav className="flex flex-col gap-1">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`px-4 py-3 text-sm font-medium rounded-sm ${
                    isActive(link.href)
                      ? 'text-teal-600 bg-teal-50'
                      : 'text-slate-700 hover:bg-slate-50'
                  }`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {link.label}
                </Link>
              ))}
              <div className="border-t border-slate-100 my-2" />
              <Link
                to="/ratgeber"
                className="px-4 py-3 text-sm font-medium text-slate-700 hover:bg-slate-50 rounded-sm"
                onClick={() => setMobileMenuOpen(false)}
              >
                Alle Ratgeber
              </Link>
              <Link
                to="/ueber-uns"
                className="px-4 py-3 text-sm font-medium text-slate-700 hover:bg-slate-50 rounded-sm"
                onClick={() => setMobileMenuOpen(false)}
              >
                Über Uns
              </Link>
              <Link
                to="/kontakt"
                className="px-4 py-3 text-sm font-medium text-slate-700 hover:bg-slate-50 rounded-sm"
                onClick={() => setMobileMenuOpen(false)}
              >
                Kontakt
              </Link>
              <div className="px-4 pt-2">
                <Link
                  to="/northwest-registered-agent-erfahrungen"
                  className="cta-primary text-sm w-full text-center"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Testsieger ansehen
                </Link>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
